import React, { useState, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import GenericModal from '../../../components/modal/GenericModal';
import InputText from '../../../components/modalComponents/InputText';
import InputUploadFile from '../../../components/modalComponents/InputUploadFile';
import SpinnerLoading from '../../../components/pageComponents/SpinnerLoading';
import { ESTADOS_FIRMA_TP } from '../../../constants/hardCodedConstants';
import { FILE_FORMATS } from '../../../constants/hardCodedConstants';
import { useGetRequest } from '../../../hooks/useGetRequest/useGetRequest';
import { useManagePostRequest } from '../../../hooks/useManagePostRequest/useManagePostRequest';
import {
    getFichaFirmaAPI,
    previsualizarDocumentoTrasladoAPI,
    subirFirmaColaboradorTrasladoAPI,
    eliminarFirmaColaboradorTrasladoAPI,
    finalizarFirmaColaboradorAPI,
} from '../../../consumers/backendApisUrls';

// ──────────────────────────────────────────────────────────────────────────
// HU-TR-07 CA-02 a CA-09: "[Campaña] – Firma colaborador".
//
// Última etapa del traslado. El colaborador revisa sus datos y los de la
// asignación, previsualiza el documento, carga su firma y lo envía a GDH.
//
// CA-17 / CA-18 / CA-24: si GDH le devolvió la tarea, la MISMA pantalla muestra
// el motivo arriba y vuelve a habilitar el envío. No hay una pantalla aparte de
// subsanación — la HU pide explícitamente "la misma estructura e información".
//
// Los botones los gobiernan los flags que arma el backend (PuedeFirmar,
// EstaObservado), no reglas recalculadas acá: mismo patrón que el resto del
// módulo con ExtraData.PuedeEditarConvocatoria.
// ──────────────────────────────────────────────────────────────────────────

const SECTION_NAME = "FirmaColaborador";

const FirmaColaborador = ({ toggleModal, refreshPage, postulante = {} }) => {
    const idPostulante = postulante?.idPostulante;

    const [executeGet] = useGetRequest();
    const [managePost] = useManagePostRequest();

    const [ficha, setFicha] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSending, setIsSending] = useState(false);
    const [firma, setFirma] = useState(null);
    const [subiendoFirma, setSubiendoFirma] = useState(false);
    const [previsualizando, setPrevisualizando] = useState(false);

    const cargarFicha = async () => {
        const respuesta = await executeGet(getFichaFirmaAPI(idPostulante));
        setFicha(respuesta?.data ?? null);
    };

    useEffect(() => {
        if (!idPostulante) return;
        let cancelado = false;
        (async () => {
            const respuesta = await executeGet(getFichaFirmaAPI(idPostulante));
            if (cancelado) return;
            setFicha(respuesta?.data ?? null);
            setIsLoading(false);
        })();
        return () => { cancelado = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idPostulante]);

    // La firma puede venir de esta sesion (estado local, recien subida) o de una
    // anterior (TieneFirmaCargada, que resuelve el backend). Sin lo segundo, cerrar y
    // reabrir el modal dejaba "Guardar y finalizar" deshabilitado con la firma ya en S3.
    const hayFirma = !!firma || ficha?.TieneFirmaCargada === true;

    // CA-05: el documento se genera al vuelo y se abre en otra pestaña. No cuenta
    // como firmado — el backend no lo persiste ni toca el estado.
    //
    // La pestaña se abre SINCRONICAMENTE, antes del await, con un mensaje de
    // "Generando documento..." — igual que SolicitarPrestamo.jsx (unico lugar
    // del código con este patron ya resuelto): abrirla despues del await deja
    // que el navegador la bloquee como popup no solicitado (23/09/2026).
    const handlePrevisualizar = async () => {
        const previewWindow = window.open('', '_blank');
        if (!previewWindow) {
            toast.warn('Permita ventanas emergentes para ver la vista previa.');
            return;
        }
        previewWindow.document.write('<p style="font-family:sans-serif;text-align:center;margin-top:40px;color:#666;">Generando documento...</p>');
        setPrevisualizando(true);
        try {
            const respuesta = await executeGet(previsualizarDocumentoTrasladoAPI(idPostulante));
            const url = respuesta?.data?.UrlS3;
            if (!url) {
                toast.error('No se pudo generar la previsualización del documento.');
                previewWindow.close();
                return;
            }
            if (respuesta?.data?.IncluyeFirma === false) {
                toast.info('Está viendo un borrador: el documento todavía no tiene su firma.');
            }
            previewWindow.location.href = url;
        } catch {
            previewWindow.close();
        } finally {
            setPrevisualizando(false);
        }
    };

    // CA-06: la firma se sube como imagen. Se persiste de inmediato contra el
    // backend (no se acumula en memoria) para que "Guardar y finalizar" solo tenga
    // que disparar la generación del documento.
    //
    // InputUploadFile llama a esta misma función con {} cuando el usuario hace
    // clic en SU PROPIO icono de basurero (ver InputUploadFile.jsx:86) — antes
    // había además un botón "Eliminar firma cargada" propio de este modal, y
    // los dos aparecían juntos apenas se subía la firma (24/09/2026: un solo
    // icono de eliminar, como en el resto de la app).
    const handleCambiarFirma = async (s3Response) => {
        if (s3Response?.Path) {
            setFirma(s3Response);
            await managePost(
                subirFirmaColaboradorTrasladoAPI(idPostulante),
                s3Response,
                async () => { await cargarFicha(); },
                false
            );
            return;
        }

        await managePost(
            eliminarFirmaColaboradorTrasladoAPI(idPostulante),
            {},
            async () => {
                setFirma(null);
                await cargarFicha();
                toast.success('Firma eliminada. Puede cargar otra.');
            },
            false
        );
    };

    // CA-09 / CA-19: genera el documento definitivo y lo manda a revisión de GDH.
    const handleGuardarYFinalizar = async () => {
        if (isSending) return;
        if (!hayFirma) {
            toast.warn('Debe cargar su firma antes de finalizar.');
            return;
        }
        setIsSending(true);
        try {
            await managePost(
                finalizarFirmaColaboradorAPI(idPostulante),
                {},
                async () => {
                    toast.success('Documento firmado y enviado a GDH para su revisión.');
                    refreshPage && refreshPage();
                    toggleModal();
                },
                false
            );
        } finally {
            setIsSending(false);
        }
    };

    const titulo = ficha?.NombreConvocatoria
        ? `${ficha.NombreConvocatoria} – Firma colaborador`
        : 'Firma colaborador';

    if (isLoading) {
        return (
            <GenericModal showModal={toggleModal} titulo="Firma colaborador" width="900px">
                <div className="modal-body text-center py-5"><SpinnerLoading /></div>
            </GenericModal>
        );
    }

    if (!ficha) {
        return (
            <GenericModal showModal={toggleModal} titulo="Firma colaborador" width="900px">
                <div className="modal-body">
                    <AvisoBox>No se pudo cargar la información del proceso.</AvisoBox>
                </div>
            </GenericModal>
        );
    }

    // HU-PR-09: promoción y traslado comparten esta pantalla; solo cambian el
    // rótulo "Traslado"/"Promoción" y que promoción además muestra el sueldo
    // nuevo (CA-04), que traslado no tiene.
    const esPromocion = ficha.TipoProceso === 'P';

    return (
        <GenericModal showModal={toggleModal} titulo={titulo} width="900px">
            <div className="modal-body pt-3">

                {/* CA-17 / CA-24: el motivo de la devolución, bien visible y arriba de
                    todo — es lo primero que el colaborador necesita leer. */}
                {ficha.EstaObservado && (
                    <ObservacionBox className="mb-3">
                        <div className="titulo">
                            <FontAwesomeIcon icon="exclamation-circle" className="mr-2" />
                            GDH observó su documento
                        </div>
                        <div className="comentario">{ficha.ComentarioObservacion}</div>
                        <small>Corrija lo indicado y vuelva a enviar el documento firmado.</small>
                    </ObservacionBox>
                )}

                {/* CA-03 */}
                <SubTitulo>Datos del colaborador</SubTitulo>
                <div className="displayFlexWrap">
                    <div className="col-md-6"><InputText label="Nombre" name="nombre" value={ficha.NombreColaborador} direction="row" isDisabled /></div>
                    <div className="col-md-6"><InputText label="DNI" name="dni" value={ficha.Dni} direction="row" isDisabled /></div>
                    <div className="col-md-6"><InputText label="Puesto actual" name="puesto" value={ficha.PuestoActual} direction="row" isDisabled /></div>
                    <div className="col-md-6"><InputText label="Especialidad" name="especialidad" value={ficha.Especialidad ?? '—'} direction="row" isDisabled /></div>
                    <div className="col-md-6"><InputText label="Sede actual" name="sedeActual" value={ficha.SedeActual} direction="row" isDisabled /></div>
                    <div className="col-md-6"><InputText label="Región" name="region" value={ficha.RegionActual ?? '—'} direction="row" isDisabled /></div>
                    <div className="col-md-6"><InputText label="Fecha de ingreso" name="fechaIngreso" value={ficha.FechaIngreso} direction="row" isDisabled /></div>
                </div>

                <hr />

                {/* CA-04 */}
                <SubTitulo>{esPromocion ? 'Condiciones de la promoción' : 'Condiciones del traslado'}</SubTitulo>
                <div className="displayFlexWrap">
                    <div className="col-md-6"><InputText label="Proceso" name="proceso" value={ficha.NombreConvocatoria} direction="row" isDisabled /></div>
                    <div className="col-md-6"><InputText label="Tipo" name="tipo" value={esPromocion ? 'Promoción' : 'Traslado'} direction="row" isDisabled /></div>
                    <div className="col-md-6"><InputText label="Puesto de destino" name="puestoDestino" value={ficha.PuestoDestino ?? '—'} direction="row" isDisabled /></div>
                    <div className="col-md-6"><InputText label="Sede de destino" name="sedeDestino" value={ficha.SedeDestino ?? '—'} direction="row" isDisabled /></div>
                    <div className="col-md-6"><InputText label="Headcount" name="headcount" value={ficha.Headcount ?? '—'} direction="row" isDisabled /></div>
                    <div className="col-md-6"><InputText label="Fecha efectiva" name="fechaEfectiva" value={ficha.FechaEfectiva} direction="row" isDisabled /></div>
                    {/* HU-PR-09 CA-04: solo aplica a promoción; en traslado ficha.SueldoPromocion viaja null. */}
                    {esPromocion && (
                        <>
                            <div className="col-md-6"><InputText label="Sueldo de promoción" name="sueldoPromocion" value={ficha.SueldoPromocion ?? '—'} direction="row" isDisabled /></div>
                            <div className="col-md-6"><InputText label="Especialidad de destino" name="especialidadRequerimiento" value={ficha.EspecialidadRequerimiento ?? '—'} direction="row" isDisabled /></div>
                        </>
                    )}
                </div>

                <hr />

                {/* CA-05 */}
                <div className="text-center mb-3">
                    <button type="button" className="btn generic-button-buscador"
                        disabled={previsualizando} onClick={handlePrevisualizar}>
                        <FontAwesomeIcon icon="file-pdf" className="mr-2" />
                        Previsualizar documento
                        {previsualizando && <FontAwesomeIcon icon="spinner" className="spinner ml-2" />}
                    </button>
                </div>

                {ficha.PuedeFirmar ? (
                    <FirmaBox>
                        <SubTitulo>Su firma</SubTitulo>

                        {/* CA-07: el texto es literal de la HU, no se parafrasea. */}
                        <AvisoBox className="mb-3">
                            Esta firma será utilizada para firmar el proceso en curso y será
                            eliminada automáticamente después de aprobar la solicitud.
                        </AvisoBox>

                        <InputUploadFile
                            label="Firma"
                            direction="column"
                            name="FirmaFileViewModel"
                            sectionName={SECTION_NAME}
                            uploadFile={firma}
                            setUploadFile={handleCambiarFirma}
                            isUploading={subiendoFirma}
                            setIsUploading={setSubiendoFirma}
                            validExtensions={FILE_FORMATS.ALLOWED_IMAGE_FORMATS}
                            showToastSuccess={false}
                            textButton="Subir firma"
                            isRequired
                        />

                        {/* Solo cuando la firma quedo cargada en una sesion anterior
                            (TieneFirmaCargada) y en esta todavia no se toco nada: acá
                            `firma` sigue null, asi que InputUploadFile no tiene el
                            archivo en memoria y no puede mostrar su propio icono de
                            eliminar (ver InputUploadFile.jsx:76). Si `firma` ya tiene
                            valor, InputUploadFile YA muestra su propio icono — no
                            duplicar (antes se mostraban los dos juntos, 24/09/2026). */}
                        {!firma && ficha?.TieneFirmaCargada && (
                            <div className="text-center mt-2">
                                <button type="button" className="btn btn-link p-0" onClick={() => handleCambiarFirma({})}>
                                    <FontAwesomeIcon icon="trash" className="mr-1" />
                                    Eliminar firma cargada
                                </button>
                            </div>
                        )}

                        <div className="d-flex justify-content-center mt-4">
                            <button type="button" className="btn generic-button-buscador generic-button-hover-verde green-innova-background"
                                disabled={isSending || !hayFirma} onClick={handleGuardarYFinalizar}>
                                Guardar y finalizar
                                {isSending && <FontAwesomeIcon icon="spinner" className="spinner ml-2" />}
                            </button>
                        </div>
                    </FirmaBox>
                ) : (
                    // Ya envió y está en manos de GDH, o el traslado cerró. En los dos
                    // casos no hay nada que hacer acá, y conviene decirlo en vez de
                    // mostrar un formulario inerte.
                    <AvisoBox>
                        {ficha.IdEstadoCiclo === ESTADOS_FIRMA_TP.CERRADO
                            ? `Su ${esPromocion ? 'promoción' : 'traslado'} ya fue aprobado y cerrado por GDH.`
                            : 'Su documento firmado está en revisión por GDH. Le avisaremos cuando haya novedades.'}
                    </AvisoBox>
                )}
            </div>
        </GenericModal>
    )
}

export default FirmaColaborador

const SubTitulo = styled.div`
    font-weight: bold;
    color: #0b2b57;
    margin-bottom: 10px;
`;

const AvisoBox = styled.div`
    border: 1px solid #ffe08a;
    background: #fff9e6;
    border-radius: 8px;
    padding: 14px;
    color: #8a6d3b;
    font-size: 14px;
`;

const ObservacionBox = styled.div`
    border: 1px solid #f5c2c7;
    background: #fdeaea;
    border-radius: 8px;
    padding: 14px;

    .titulo {
        font-weight: bold;
        color: #b02a2a;
        margin-bottom: 6px;
    }

    .comentario {
        color: #292929;
        white-space: pre-wrap;
        margin-bottom: 6px;
    }

    small {
        color: #8a6d3b;
    }
`;

const FirmaBox = styled.div`
    border: 1px solid #dee2e6;
    border-radius: 8px;
    padding: 20px;
`;
